import { useState } from "react";
import {
  villaRentSchema, VILLA_LOCATIONS, VILLA_BUDGETS,
  villaLocationLabels, villaBudgetLabels, type VillaRentPayload,
} from "@anton/shared";
import { PageHeader } from "../components/PageHeader";
import { Field } from "../components/Field";
import { RadioGroup } from "../components/RadioGroup";
import { SubmitButton } from "../components/SubmitButton";
import { SuccessScreen } from "../components/SuccessScreen";
import { Card, Lead, H3, CheckList, SectionHeader } from "../components/Section";
import { DatePicker } from "../components/DatePicker";
import { submitApplication } from "../lib/api";
import { haptic, showAlert } from "../lib/telegram";

export default function VillaRent() {
  const [form, setForm] = useState<Partial<VillaRentPayload>>({ adults: 2, kids: 0, bedrooms: 2 });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (done) return <SuccessScreen message="Подберём варианты под твой запрос и свяжемся в ближайшее время." />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = villaRentSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const i of parsed.error.issues) fe[i.path.join(".")] = i.message;
      setErrors(fe);
      haptic("error");
      return;
    }
    setLoading(true);
    const res = await submitApplication("VILLA_RENT", parsed.data);
    setLoading(false);
    if (!res.ok) { showAlert(res.error ?? "Ошибка"); haptic("error"); return; }
    setDone(true);
  }

  return (
    <div className="pb-10">
      <PageHeader
        eyebrow="Аренда вилл на Бали"
        title="Жизнь на острове в идеальной частной вилле"
        subtitle="Подберу премиальную виллу в самой подходящей локации. От недели до года."
        icon="🏖"
        iconColor="mint"
      />
      <div className="px-5 mt-3 space-y-3">
        <Card>
          <Lead>
            Бали — это не просто направление, это образ жизни.
            Подбираю виллы для аренды в самых элитных локациях острова — для отпуска, ретрита или долгой жизни в раю.
          </Lead>
        </Card>

        <Card>
          <H3>Локации</H3>
          <div className="space-y-2 mt-2">
            <div className="ios-callout-muted">
              <b>🏖 Семиньяк</b> — шик у океана, пляжные клубы, рестораны, ночная жизнь.
            </div>
            <div className="ios-callout-muted">
              <b>🌊 Улувату</b> — виллы на скалах с инфинити-бассейнами и видом на океан, спот-серф, Bulgari и Alila рядом.
            </div>
            <div className="ios-callout-muted">
              <b>🌾 Чангу</b> — рисовые поля, быстрый Wi-Fi, рабочие зоны. Идеально для digital nomad.
            </div>
            <div className="ios-callout-muted">
              <b>🌿 Убуд</b> — джунгли, open-air пространства, йога, спа, ретриты.
            </div>
          </div>
        </Card>

        <Card>
          <H3>Что получаешь</H3>
          <CheckList items={[
            "Приватность и пространство — собственный мир, без толп в отеле",
            "Персональный сервис: повар, хаускипер, менеджер виллы",
            "Премиум-удобства: инфинити-бассейны, домашние кинотеатры, спортзалы",
            "Трансферы из аэропорта, спа в вилле, авторские туры",
          ]} />
        </Card>

        <div className="ios-callout">
          ✨ Подбор бесплатный для тебя. Заполни форму — пришлю 2–3 варианта.
        </div>
      </div>

      <SectionHeader>Анкета подбора</SectionHeader>
      <form noValidate onSubmit={onSubmit} className="px-5 space-y-4">
        <Field label="Имя" error={errors["name"]}>
          <input className={`ios-input ${errors["name"] ? "ios-input-error" : ""}`}
            value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Контакт (Telegram / WhatsApp / телефон)" error={errors["contact"]}>
          <input className={`ios-input ${errors["contact"] ? "ios-input-error" : ""}`}
            value={form.contact ?? ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
        </Field>
        <Field label="Локация" error={errors["location"]}>
          <RadioGroup value={form.location} onChange={(v) => setForm({ ...form, location: v as any })}
            options={VILLA_LOCATIONS.map((v) => ({ value: v, label: villaLocationLabels[v] }))} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Заезд" error={errors["checkIn"]}>
            <DatePicker
              value={form.checkIn}
              onChange={(v) => setForm({ ...form, checkIn: v })}
              placeholder="Когда"
              error={!!errors["checkIn"]}
            />
          </Field>
          <Field label="Выезд" error={errors["checkOut"]}>
            <DatePicker
              value={form.checkOut}
              onChange={(v) => setForm({ ...form, checkOut: v })}
              placeholder="Когда"
              error={!!errors["checkOut"]}
            />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Взрослые" error={errors["adults"]}>
            <input type="number" min={1} className={`ios-input ${errors["adults"] ? "ios-input-error" : ""}`}
              value={form.adults ?? ""} onChange={(e) => setForm({ ...form, adults: Number(e.target.value) })} />
          </Field>
          <Field label="Дети" error={errors["kids"]}>
            <input type="number" min={0} className={`ios-input ${errors["kids"] ? "ios-input-error" : ""}`}
              value={form.kids ?? 0} onChange={(e) => setForm({ ...form, kids: Number(e.target.value) })} />
          </Field>
          <Field label="Спален" error={errors["bedrooms"]}>
            <input type="number" min={1} className={`ios-input ${errors["bedrooms"] ? "ios-input-error" : ""}`}
              value={form.bedrooms ?? ""} onChange={(e) => setForm({ ...form, bedrooms: Number(e.target.value) })} />
          </Field>
        </div>
        <Field label="Бюджет в месяц" error={errors["budget"]}>
          <RadioGroup value={form.budget} onChange={(v) => setForm({ ...form, budget: v as any })}
            options={VILLA_BUDGETS.map((v) => ({ value: v, label: villaBudgetLabels[v] }))} />
        </Field>
        <Field label="Особые пожелания" hint="опционально" error={errors["specialRequests"]}>
          <textarea rows={3} className={`ios-input ${errors["specialRequests"] ? "ios-input-error" : ""}`}
            value={form.specialRequests ?? ""}
            onChange={(e) => setForm({ ...form, specialRequests: e.target.value })}
            placeholder="повар, спорт, питомцы, рабочее место…" />
        </Field>
        <div className="pt-2">
          <SubmitButton loading={loading}>Подобрать виллу</SubmitButton>
        </div>
      </form>
    </div>
  );
}
